/**
 * SubscriptionWidget - Class-based implementation for handling Shopify selling plans
 * Manages subscription options, pricing, and cart form updates
 *
 * Default Behavior:
 * One-time purchase is always the default selection
 * This ensures consistent behavior across all product pages
 */
class SubscriptionWidget {
  constructor() {
    // State variables
    this.currentVariant = null;
    this.previousVariantId = null;
    this.selectedSellingPlanId = null;
    this.isSubscriptionSelected = false;
    this.sellingPlanGroups = [];
    this.productForm = null;
    this.currencySymbol = window.shopCurrency?.symbol || '$';

    // DOM elements
    this.widget = null;
    this.oneTimeRadio = null;
    this.subscriptionRadio = null;
    this.frequencySelect = null;
    this.frequencyButtons = null;
    this.subscriptionDetails = null;
    this.toggleOptions = null;
    this.sellingPlanInput = null;
    this.atcButtonPrice = null;
    this.atcButtonComparePrice = null;
    this.variantFrequencyButtons = null;
    this.truemedWidgetWrapper = null;

    // What's Included elements
    this.whatsIncludedName = null;
    this.whatsIncludedPrice = null;
    this.subscriptionMessage = null;
    this.subscriptionMessagePrice = null;
    this.subscriptionMessageFrequency = null;
    this.subscriptionMessageProductName = null;
    this.currentFrequency = '3 months';

    // Find delivery date element
    this.nextDeliveryDateValue = null;

    // Find recurring payment text element
    this.recurringPaymentText = null;

    // Subscription discount text element
    this.subscriptionDiscountText = null;

    // Bind methods to maintain 'this' context
    this.init = this.init.bind(this);
    this.initializeDefaultState = this.initializeDefaultState.bind(this);
    this.handlePurchaseTypeChange = this.handlePurchaseTypeChange.bind(this);
    this.handleFrequencyChange = this.handleFrequencyChange.bind(this);
    this.handleFrequencyButtonClick =
      this.handleFrequencyButtonClick.bind(this);
    this.handleToggleOptionClick = this.handleToggleOptionClick.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.updatePricing = this.updatePricing.bind(this);
    this.updateAtcButtonPrice = this.updateAtcButtonPrice.bind(this);
    this.findSellingPlanById = this.findSellingPlanById.bind(this);
    this.formatMoney = this.formatMoney.bind(this);
    this.updateWhatsIncluded = this.updateWhatsIncluded.bind(this);
    this.updateSubscriptionDiscountLabel =
      this.updateSubscriptionDiscountLabel.bind(this);

    // Initialize on DOM content loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.init);
    } else {
      this.init();
    }
  }

  /**
   * Initialize the subscription widget
   * Set up event listeners and initial state
   */
  init() {
    console.log('Initializing SubscriptionWidget');

    // Find the widget in the DOM
    this.widget = document.querySelector('.subscription-widget');
    if (!this.widget) {
      console.error('Subscription widget not found in DOM');
      return;
    }

    // Get selling plan data from the widget
    try {
      this.sellingPlanGroups = JSON.parse(
        this.widget.dataset.sellingPlanGroups || '[]'
      );
      if (this.sellingPlanGroups.length === 0) {
        console.warn('No selling plan groups found');
      } else {
        console.log('Found selling plan groups:', this.sellingPlanGroups);
      }
    } catch (e) {
      console.error('Error parsing selling plan groups:', e);
      return;
    }

    // Find the product form
    this.productForm = document.querySelector('form[action="/cart/add"]');
    if (!this.productForm) {
      console.error('Product form not found');
      return;
    }

    // Get the current variant ID
    const variantIdInput = this.productForm.querySelector('input[name="id"]');
    if (variantIdInput) {
      const currentVariantId = variantIdInput.value;
      this.currentVariant = window.productData?.variants.find(
        v => v.id.toString() === currentVariantId
      );
      // Set initial variant ID to track changes
      if (this.currentVariant) {
        this.previousVariantId = this.currentVariant.id;
      }
      console.log('Current variant:', this.currentVariant);
    }

    // Find DOM elements
    this.oneTimeRadio = this.widget.querySelector(
      'input[name="purchase_option"][value="one_time"]'
    );
    this.subscriptionRadio = this.widget.querySelector(
      'input[name="purchase_option"][value="subscription"]'
    );
    this.frequencySelect = this.widget.querySelector(
      '.subscription-details__frequency-select'
    );
    this.frequencyButtons = this.widget.querySelectorAll(
      '.subscription-details__frequency-button'
    );
    this.subscriptionDetails = this.widget.querySelector(
      '.subscription-details'
    );
    this.toggleOptions = this.widget.querySelectorAll(
      '.subscription-toggle__option'
    );

    // Find ATC button price elements
    this.atcButtonPrice = document.getElementById('atc_button_price');
    this.atcButtonComparePrice = document.getElementById(
      'atc_button_compare_price'
    );

    // Find What's Included elements
    this.whatsIncludedName = document.querySelector(
      '.fluoride-whats-included__name'
    );
    this.whatsIncludedPrice = document.querySelector(
      '.fluoride-whats-included__price'
    );
    this.subscriptionMessage = document.getElementById('subscription-message');
    this.subscriptionMessagePrice =
      document.getElementById('subscription-price');
    this.subscriptionMessageFrequency = document.getElementById(
      'subscription-frequency'
    );
    this.subscriptionMessageProductName = document.getElementById(
      'subscription-product-name'
    );
    this.variantFrequencyButtons = document.querySelectorAll(
      '.subscription-details__frequency-buttons'
    );

    // Find delivery date element
    this.nextDeliveryDateValue = document.querySelector(
      '.subscription-details__delivery-date-value strong'
    );

    // Find subscription discount text element
    this.subscriptionDiscountText = document.getElementById(
      'subscription-discount-text'
    );

    // Create or find the selling plan input for the form
    let sellingPlanInput = this.productForm.querySelector(
      'input[name="selling_plan"]'
    );
    if (!sellingPlanInput) {
      sellingPlanInput = document.createElement('input');
      sellingPlanInput.type = 'hidden';
      sellingPlanInput.name = 'selling_plan';
      this.productForm.appendChild(sellingPlanInput);
    }
    this.sellingPlanInput = sellingPlanInput;

    // Add event listeners
    if (this.oneTimeRadio && this.subscriptionRadio) {
      this.oneTimeRadio.addEventListener(
        'change',
        this.handlePurchaseTypeChange
      );
      this.subscriptionRadio.addEventListener(
        'change',
        this.handlePurchaseTypeChange
      );
    }

    if (this.frequencySelect) {
      this.frequencySelect.addEventListener(
        'change',
        this.handleFrequencyChange
      );
    }

    // Add click handlers for frequency buttons
    if (this.frequencyButtons) {
      this.frequencyButtons.forEach(button => {
        button.addEventListener('click', this.handleFrequencyButtonClick);
      });
    }

    // Add click handlers for toggle options
    if (this.toggleOptions) {
      this.toggleOptions.forEach(option => {
        option.addEventListener('click', this.handleToggleOptionClick);
      });
    }

    // Listen for variant changes
    document.addEventListener('product:variant-change', event => {
      const newVariantId = event.detail?.variant?.id;
      const variantIdChanged =
        newVariantId && newVariantId !== this.previousVariantId;

      this.handleVariantChange(event);

      // Only show variant frequency buttons when variant ID actually changes
      if (variantIdChanged) {
        this.showVariantFrequencyButtons();
        this.previousVariantId = newVariantId;
      }
    });

    // Show the correct variant's frequency buttons on initial load first
    if (this.currentVariant) {
      this.showVariantFrequencyButtons();
    }

    // Set initial state AFTER showing frequency buttons to override any unwanted state changes
    this.initializeDefaultState();

    // If there's a default selling plan, set it
    if (this.frequencyButtons && this.frequencyButtons.length > 0) {
      const firstButton = this.frequencyButtons[0];
      if (firstButton && firstButton.dataset.sellingPlanId) {
        this.selectedSellingPlanId = firstButton.dataset.sellingPlanId;

        // Get initial frequency text
        this.currentFrequency = this.extractFrequency(
          firstButton.textContent.trim()
        );

        // Calculate initial next delivery date
        this.updateNextDeliveryDate();
      }
    }

    // Update What's Included section
    this.updateWhatsIncluded();

    this.truemedWidgetWrapper = document.querySelector(
      '.truemed-widget-wrapper'
    );

    console.log('SubscriptionWidget initialized');
  }

  /**
   * Handle click on toggle option
   * @param {Event} event - The click event
   */
  handleToggleOptionClick(event) {
    const option = event.currentTarget;
    const radio = option.querySelector('.subscription-toggle__radio');

    if (radio) {
      radio.checked = true;

      // Trigger the change event
      const changeEvent = new Event('change', { bubbles: true });
      radio.dispatchEvent(changeEvent);
    }
  }

  /**
   * Update active state for toggle options
   */
  updateToggleOptionActiveState() {
    if (this.toggleOptions) {
      this.toggleOptions.forEach(option => {
        const radio = option.querySelector('.subscription-toggle__radio');
        if (radio && radio.checked) {
          option.classList.add('active');
        } else {
          option.classList.remove('active');
        }
      });
    }
  }

  /**
   * Initialize default state - one-time purchase is the default
   * This ensures one-time purchase is always selected by default
   */
  initializeDefaultState() {
    console.log('Initializing with one-time purchase as default');

    // Set internal state
    this.isSubscriptionSelected = false;
    this.selectedSellingPlanId = null;

    // Ensure one-time radio is checked
    if (this.oneTimeRadio && this.subscriptionRadio) {
      this.oneTimeRadio.checked = true;
      this.subscriptionRadio.checked = false;
    }

    // Clear selling plan from form
    if (this.sellingPlanInput) {
      this.sellingPlanInput.value = '';
    }

    // Update visual states based on radio button state
    this.updateToggleOptionActiveState();

    // Hide subscription details
    if (this.subscriptionDetails) {
      this.subscriptionDetails.classList.remove('active');
    }

    // Update pricing and components
    this.updatePricing();
    this.updateWhatsIncluded();

    console.log('One-time purchase set as default');
  }

  /**
   * Handle click on frequency button
   * @param {Event} event - The click event
   */
  handleFrequencyButtonClick(event) {
    const button = event.currentTarget;
    const sellingPlanId = button.dataset.sellingPlanId;

    if (sellingPlanId) {
      // Update the hidden select
      if (this.frequencySelect) {
        this.frequencySelect.value = sellingPlanId;

        // Trigger the change event
        const changeEvent = new Event('change', { bubbles: true });
        this.frequencySelect.dispatchEvent(changeEvent);
      }

      // Update active state
      if (this.frequencyButtons) {
        this.frequencyButtons.forEach(btn => {
          btn.classList.remove('active');
        });
        button.classList.add('active');
      }

      // Update the current frequency text
      this.currentFrequency = this.extractFrequency(button.textContent);

      // Calculate and update next delivery date
      this.updateNextDeliveryDate();

      // Update subscription discount label
      this.updateSubscriptionDiscountLabel();

      // Update What's Included section
      this.updateWhatsIncluded();
    }
  }

  /**
   * Handle change between one-time purchase and subscription
   * @param {Event} event - The change event
   */
  handlePurchaseTypeChange(event) {
    console.log('Purchase type changed:', event.target.value);

    this.isSubscriptionSelected = event.target.value === 'subscription';

    // Update active state for toggle options
    this.updateToggleOptionActiveState();
    this.truemedWidgetWrapper?.classList.toggle(
      'is-hidden',
      this.isSubscriptionSelected
    );

    // Toggle subscription details visibility
    if (this.subscriptionDetails) {
      if (this.isSubscriptionSelected) {
        this.subscriptionDetails.classList.add('active');

        // Update next delivery date when subscription is selected
        this.updateNextDeliveryDate();
      } else {
        this.subscriptionDetails.classList.remove('active');
      }
    }

    // Update the selling plan ID in the form
    if (
      this.isSubscriptionSelected &&
      this.frequencySelect &&
      this.frequencySelect.value
    ) {
      this.selectedSellingPlanId = this.frequencySelect.value;
      if (this.sellingPlanInput) {
        this.sellingPlanInput.value = this.selectedSellingPlanId;
      }
    } else {
      this.selectedSellingPlanId = null;
      if (this.sellingPlanInput) {
        this.sellingPlanInput.value = '';
      }
    }

    // Update pricing display
    this.updatePricing();

    // Update What's Included section
    this.updateWhatsIncluded();
  }

  /**
   * Handle frequency selection change
   * @param {Event} event - The change event
   */
  handleFrequencyChange(event) {
    console.log('Frequency changed:', event.target.value);

    if (this.isSubscriptionSelected) {
      this.selectedSellingPlanId = event.target.value;

      // Update the form input
      if (this.sellingPlanInput) {
        this.sellingPlanInput.value = this.selectedSellingPlanId;
      }

      // Update the current frequency text
      if (event.target.selectedOptions && event.target.selectedOptions[0]) {
        this.currentFrequency = this.extractFrequency(
          event.target.selectedOptions[0].textContent
        );
      } else {
        // Try to find the active button
        const activeButton = document.querySelector(
          '.subscription-details__frequency-button.active'
        );
        if (activeButton) {
          this.currentFrequency = this.extractFrequency(
            activeButton.textContent
          );
        }
      }

      // Calculate and update next delivery date
      this.updateNextDeliveryDate();

      // Update pricing display
      this.updatePricing();

      // Update What's Included section
      this.updateWhatsIncluded();
    }
  }

  /**
   * Handle variant change events
   * @param {CustomEvent} event - The variant change event
   */
  handleVariantChange(event) {
    console.log('Variant changed:', event.detail.variant);

    this.currentVariant = event.detail.variant;

    // Update pricing display
    this.updatePricing();

    // Update toggle option prices
    this.updateToggleOptionPrices();

    // Update subscription discount label
    this.updateSubscriptionDiscountLabel();

    // Update What's Included section
    this.updateWhatsIncluded();
  }

  showVariantFrequencyButtons() {
    if (!this.currentVariant) return;

    this.variantFrequencyButtons.forEach(div => {
      if (div.dataset.variantId == this.currentVariant.id) {
        div.style.display = 'flex';
        div.querySelector('.subscription-details__frequency-button').click();
      } else {
        div.style.display = 'none';
      }
    });

    // Update discount label after showing new variant buttons
    this.updateSubscriptionDiscountLabel();
  }

  /**
   * Update prices in the toggle options
   */
  updateToggleOptionPrices() {
    if (!this.currentVariant) return;

    // Update one-time price
    const oneTimeOption = this.widget.querySelector(
      '.subscription-toggle__option[data-option="one_time"]'
    );
    if (oneTimeOption) {
      const priceElement = oneTimeOption.querySelector(
        '.subscription-toggle__price'
      );
      if (priceElement) {
        priceElement.textContent = this.formatMoneyWithoutCurrency(
          this.currentVariant.price
        );
      }
    }

    // Update subscription price
    const subscriptionOption = this.widget.querySelector(
      '.subscription-toggle__option[data-option="subscription"]'
    );
    if (subscriptionOption && this.selectedSellingPlanId) {
      const priceElement = subscriptionOption.querySelector(
        '.subscription-toggle__price'
      );

      if (priceElement) {
        const sellingPlan = this.findSellingPlanById(
          this.selectedSellingPlanId
        );
        if (
          sellingPlan &&
          sellingPlan.price_adjustments &&
          sellingPlan.price_adjustments.length > 0
        ) {
          const adjustment = sellingPlan.price_adjustments[0];

          if (adjustment.value_type === 'percentage') {
            const discountedPrice = this.calculateDiscountedPrice(
              this.currentVariant.price,
              sellingPlan
            );

            // Clear existing content
            priceElement.innerHTML = '';

            // Create original price element
            const originalPriceEl = document.createElement('span');
            originalPriceEl.classList.add(
              'subscription-toggle__original-price'
            );
            originalPriceEl.textContent = this.formatMoneyWithoutCurrency(
              this.currentVariant.price
            );

            // Create discounted price element
            const discountedPriceEl = document.createElement('span');
            discountedPriceEl.classList.add(
              'subscription-toggle__discounted-price'
            );
            discountedPriceEl.textContent =
              this.formatMoneyWithoutCurrency(discountedPrice);

            // Create save element
            const saveEl = document.createElement('span');
            saveEl.classList.add('subscription-toggle__save');
            saveEl.textContent = `Save ${adjustment.value}%`;

            // Append elements
            priceElement.appendChild(originalPriceEl);
            priceElement.appendChild(discountedPriceEl);
            priceElement.appendChild(saveEl);
          } else {
            priceElement.textContent = this.formatMoneyWithoutCurrency(
              this.currentVariant.price
            );
          }
        } else {
          priceElement.textContent = this.formatMoneyWithoutCurrency(
            this.currentVariant.price
          );
        }
      }
    }
  }

  /**
   * Update pricing display based on current selection
   */
  updatePricing() {
    if (!this.currentVariant) return;

    // Update toggle option prices
    this.updateToggleOptionPrices();

    // Update ATC button price
    this.updateAtcButtonPrice();
  }

  /**
   * Update the Add to Cart button price
   */
  updateAtcButtonPrice() {
    if (!this.currentVariant || !this.atcButtonPrice) return;

    let displayPrice = this.currentVariant.price;

    // If subscription is selected, calculate the discounted price
    if (this.isSubscriptionSelected && this.selectedSellingPlanId) {
      const sellingPlan = this.findSellingPlanById(this.selectedSellingPlanId);
      if (
        sellingPlan &&
        sellingPlan.price_adjustments &&
        sellingPlan.price_adjustments.length > 0
      ) {
        const adjustment = sellingPlan.price_adjustments[0];

        if (adjustment.value_type === 'percentage') {
          displayPrice = this.calculateDiscountedPrice(
            this.currentVariant.price,
            sellingPlan
          );
        }
      }
    }

    // Update ATC button price
    this.atcButtonPrice.textContent =
      this.formatMoneyWithoutCurrency(displayPrice);

    // Update compare price if it exists
    if (this.atcButtonComparePrice) {
      if (
        this.isSubscriptionSelected &&
        this.currentVariant.price > displayPrice
      ) {
        // Show the original price as compare price when subscription discount is applied
        this.atcButtonComparePrice.textContent =
          this.formatMoneyWithoutCurrency(this.currentVariant.price);
        this.atcButtonComparePrice.style.display = 'inline-block';
      } else if (this.currentVariant.compare_at_price) {
        // Show the variant's compare price if it exists
        this.atcButtonComparePrice.textContent =
          this.formatMoneyWithoutCurrency(this.currentVariant.compare_at_price);
        this.atcButtonComparePrice.style.display = 'inline-block';
      } else {
        // Hide compare price if there's no discount
        this.atcButtonComparePrice.style.display = 'none';
      }
    }
  }

  /**
   * Find a selling plan by its ID
   * @param {string} id - The selling plan ID
   * @returns {Object|null} - The selling plan object or null if not found
   */
  findSellingPlanById(id) {
    if (!id || !this.sellingPlanGroups || this.sellingPlanGroups.length === 0) {
      return null;
    }

    for (const group of this.sellingPlanGroups) {
      if (group.selling_plans) {
        for (const plan of group.selling_plans) {
          if (plan.id.toString() === id.toString()) {
            return plan;
          }
        }
      }
    }

    return null;
  }

  /**
   * Calculate discounted price based on selling plan
   * @param {number} regularPrice - The regular price in cents
   * @param {Object} sellingPlan - The selling plan object
   * @returns {number} - The discounted price in cents
   */
  calculateDiscountedPrice(regularPrice, sellingPlan) {
    if (
      !sellingPlan ||
      !sellingPlan.price_adjustments ||
      sellingPlan.price_adjustments.length === 0
    ) {
      return regularPrice;
    }

    // Get the first price adjustment (Shopify typically has one per plan)
    const adjustment = sellingPlan.price_adjustments[0];

    if (adjustment.value_type === 'percentage') {
      // Percentage discount
      return regularPrice * (1 - adjustment.value / 100);
    } else if (adjustment.value_type === 'fixed_amount') {
      // Fixed amount discount
      return Math.max(0, regularPrice - adjustment.value);
    } else if (adjustment.value_type === 'price') {
      // Specific price
      return adjustment.value;
    }

    return regularPrice;
  }

  /**
   * Format money value for display
   * @param {number} cents - The price in cents
   * @returns {string} - Formatted price string
   */
  formatMoney(cents) {
    if (typeof window.formatPriceWithCode === 'function') {
      return window.formatPriceWithCode(cents / 100);
    }

    return this.currencySymbol + Math.round(cents / 100);
  }

  /**
   * Format money value for display without currency symbol
   * @param {number} cents - The price in cents
   * @returns {string} - Formatted price string with currency
   */
  formatMoneyWithoutCurrency(cents) {
    return this.currencySymbol + Math.round(cents / 100);
  }

  /**
   * Extract frequency text from button or option text
   * @param {string} text - The button or option text
   * @returns {string} - The cleaned frequency text
   */
  extractFrequency(text) {
    if (!text) return '3 months';

    // First check for patterns like "3 months" or "3 month"
    const monthPattern = /(\d+)\s+month(s)?/i;
    const monthMatch = text.match(monthPattern);
    if (monthMatch) {
      const number = monthMatch[1];
      const unit = monthMatch[2] ? 'months' : 'month';
      return `${number} ${unit}`;
    }

    // If that doesn't work, check if it contains "Every"
    if (text.toLowerCase().includes('every')) {
      const match = text.match(/every\s+(.+?)([\s\(]|$)/i);
      if (match && match[1]) {
        return match[1].trim();
      }
    }

    // Otherwise, just clean up the text and remove anything in parentheses
    return text
      .replace(/deliver every/i, '')
      .replace(/\s*\([^)]*\)/g, '') // Remove text in parentheses
      .replace(/\s*save\s+\d+%/gi, '') // Remove "Save X%" text
      .trim();
  }

  /**
   * Update the What's Included section
   */
  updateWhatsIncluded() {
    // Skip if elements are not found
    if (
      !this.whatsIncludedName ||
      !this.whatsIncludedPrice ||
      !this.currentVariant
    ) {
      return;
    }

    // Determine product name based on variant
    let productName = 'Set of 2 Fluoride Filters (Qty 1)';
    if (
      this.currentVariant.title.includes('Refill') ||
      (this.currentVariant.option1 &&
        this.currentVariant.option1 === 'Refill Pack')
    ) {
      productName = 'Set of 2 Fluoride Filters Shells (Qty 1)';
    }

    // Update product name
    this.whatsIncludedName.textContent = productName;

    // Get price based on subscription status
    let displayPrice = this.currentVariant.price;
    if (this.isSubscriptionSelected && this.selectedSellingPlanId) {
      // Use the same discount calculation as the subscription widget
      const sellingPlan = this.findSellingPlanById(this.selectedSellingPlanId);
      if (
        sellingPlan &&
        sellingPlan.price_adjustments &&
        sellingPlan.price_adjustments.length > 0
      ) {
        displayPrice = this.calculateDiscountedPrice(
          this.currentVariant.price,
          sellingPlan
        );
      }
    }

    // Update price display
    this.whatsIncludedPrice.textContent =
      this.formatMoneyWithoutCurrency(displayPrice);

    // Update subscription message
    if (this.subscriptionMessage) {
      if (this.isSubscriptionSelected) {
        // Show subscription message
        this.subscriptionMessage.style.display = 'flex';

        // Update message details
        if (this.subscriptionMessagePrice) {
          this.subscriptionMessagePrice.textContent =
            this.formatMoneyWithoutCurrency(displayPrice);
        }

        if (this.subscriptionMessageProductName) {
          this.subscriptionMessageProductName.textContent = productName;
        }

        if (this.subscriptionMessageFrequency) {
          this.subscriptionMessageFrequency.textContent = this.currentFrequency;
        }
      } else {
        // Hide subscription message
        this.subscriptionMessage.style.display = 'none';
      }
    }
  }

  /**
   * Calculate and update the next delivery date based on current frequency
   */
  updateNextDeliveryDate() {
    if (!this.nextDeliveryDateValue || !this.isSubscriptionSelected) return;

    // Extract the number and unit from the frequency (e.g., "3 months" → 3, "months")
    const frequencyMatch = this.currentFrequency.match(/(\d+)\s+(\w+)/);
    if (!frequencyMatch) return;

    const amount = parseInt(frequencyMatch[1]);
    const unit = frequencyMatch[2].toLowerCase();

    // Calculate the next delivery date
    const today = new Date();
    let nextDate = new Date(today);

    // Add the appropriate time based on the unit
    if (unit.includes('month')) {
      nextDate.setMonth(today.getMonth() + amount);
    } else if (unit.includes('week')) {
      nextDate.setDate(today.getDate() + amount * 7);
    } else if (unit.includes('day')) {
      nextDate.setDate(today.getDate() + amount);
    } else if (unit.includes('year')) {
      nextDate.setFullYear(today.getFullYear() + amount);
    }

    // Format the date for display
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    const formattedDate = nextDate.toLocaleDateString('en-US', options);

    // Update the display
    this.nextDeliveryDateValue.textContent = formattedDate;
  }

  /**
   * Update the subscription discount label based on the selected selling plan
   */
  updateSubscriptionDiscountLabel() {
    if (!this.subscriptionDiscountText) return;

    // Get the active frequency button to find the current discount
    const activeButton = document.querySelector(
      '.subscription-details__frequency-button.active'
    );

    if (activeButton && activeButton.dataset.sellingPlanId) {
      const sellingPlan = this.findSellingPlanById(
        activeButton.dataset.sellingPlanId
      );

      if (
        sellingPlan &&
        sellingPlan.price_adjustments &&
        sellingPlan.price_adjustments.length > 0
      ) {
        const adjustment = sellingPlan.price_adjustments[0];

        if (adjustment.value_type === 'percentage') {
          this.subscriptionDiscountText.textContent = adjustment.value + '%';
        } else if (adjustment.value_type === 'fixed_amount') {
          const roundedAdjustment = Math.round(adjustment.value / 100);
          this.subscriptionDiscountText.textContent =
            this.currencySymbol + roundedAdjustment;
        } else {
          // Default to empty if we can't determine the discount
          this.subscriptionDiscountText.textContent = '';
        }
      }
    }
  }
}

// Initialize the subscription widget
document.addEventListener('DOMContentLoaded', () => {
  window.subscriptionWidget = new SubscriptionWidget();
});
